import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  selectUser,
  selectAdminAuth,
  setAdminAuth,
} from "../../redux/features/appData";
import { apiGetWorkspaceLinkRecords } from "../../services/KnowledgeBaseService";
import { apiVerifyAdminSecret } from "../../services/AdminService";
import { KnowledgeBaseTable } from "./KnowledgeBaseTable";
import { Theme } from "@radix-ui/themes";
import "@radix-ui/themes/styles.css";
import { LinkRecordFull } from "winslow-core";
import "./KnowledgeBasePage.css";

export const KnowledgeBasePage: React.FC = () => {
  const dispatch = useDispatch();
  const storeUser = useSelector(selectUser);
  const adminAuth = useSelector(selectAdminAuth);
  const [linkRecords, setLinkRecords] = useState<LinkRecordFull[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [adminSecret, setAdminSecret] = useState<string>("");
  const [verifying, setVerifying] = useState<boolean>(false);

  const handleAdminVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setVerifying(true);
    setError(null);

    try {
      const verified = await apiVerifyAdminSecret(adminSecret);
      if (verified) {
        dispatch(
          setAdminAuth({
            isAuthenticated: true,
            adminSecret: adminSecret,
            verifiedAt: new Date(),
          })
        );
        setAdminSecret("");
      } else {
        setError("Invalid admin secret");
      }
    } catch (err) {
      setError("Failed to verify admin secret");
    } finally {
      setVerifying(false);
    }
  };

  useEffect(() => {
    if (!adminAuth.isAuthenticated) {
      setLoading(false);
      return;
    }
    const fetchLinkRecords = async () => {
      if (!storeUser?.currentWorkspaceId) {
        setError("No workspace selected");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await apiGetWorkspaceLinkRecords(
          storeUser.currentWorkspaceId
        );
        if (data) {
          setLinkRecords(data);
        } else {
          setLinkRecords([]);
        }
      } catch (err) {
        console.error("Error fetching link records:", err);
        setError("Failed to load knowledge base");
      } finally {
        setLoading(false);
      }
    };

    fetchLinkRecords();
  }, [storeUser?.currentWorkspaceId, adminAuth.isAuthenticated]);

  if (!adminAuth.isAuthenticated) {
    return (
      <Theme>
        <div className="knowledge-base-page">
          <div className="knowledge-base-page-header">
            <h1>Admin Access Required</h1>
          </div>
          <div className="knowledge-base-page-content">
            <form
              onSubmit={handleAdminVerification}
              className="admin-auth-form">
              <div className="admin-auth-field">
                <label htmlFor="adminSecret">Admin Secret:</label>
                <input
                  type="password"
                  id="adminSecret"
                  value={adminSecret}
                  onChange={(e) => setAdminSecret(e.target.value)}
                  placeholder="Enter admin secret"
                  required
                />
              </div>
              <button type="submit" disabled={verifying}>
                {verifying ? "Verifying..." : "Access Admin Panel"}
              </button>
            </form>
            {error && <div className="knowledge-base-page-error">{error}</div>}
          </div>
        </div>
      </Theme>
    );
  }

  if (loading) {
    return (
      <Theme>
        <div className="knowledge-base-page">
          <div className="knowledge-base-page-header">
            <h1>Knowledge Base</h1>
          </div>
          <div className="knowledge-base-page-loading">
            Loading knowledge base...
          </div>
        </div>
      </Theme>
    );
  }

  if (error) {
    return (
      <Theme>
        <div className="knowledge-base-page">
          <div className="knowledge-base-page-header">
            <h1>Knowledge Base</h1>
          </div>
          <div className="knowledge-base-page-error">{error}</div>
        </div>
      </Theme>
    );
  }

  return (
    <Theme>
      <div className="knowledge-base-page">
        <div className="knowledge-base-page-header">
          <h1>Knowledge Base</h1>
          <div className="knowledge-base-page-stats">
            {linkRecords.length} document{linkRecords.length !== 1 ? "s" : ""}
          </div>
        </div>
        <div className="knowledge-base-page-content">
          <KnowledgeBaseTable linkRecords={linkRecords} />
        </div>
      </div>
    </Theme>
  );
};

